import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../../components/ui/dialog";
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger
} from "../../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from "../../components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { 
  FileText, Plus, X, Edit, Trash2, Download, FileSpreadsheet, FileJson, FileType, File,
  MoreHorizontal, ChevronsUpDown, Check, ArrowLeft, Printer, Search, RefreshCw, ChevronLeft, ChevronRight
} from "lucide-react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

const API = process.env.REACT_APP_BACKEND_URL;
const clasesContribuyente = ["Contribuyente con LAE", "Contribuyente sin LAE"];
const opcionesLindero = [
  { value: "Norte", label: "Norte" },
  { value: "Sur", label: "Sur" },
  { value: "Este", label: "Este" },
  { value: "Oeste", label: "Oeste" },
  { value: "Sur-Oeste", label: "Sur-Oeste" },
  { value: "Sur-Este", label: "Sur-Este" },
  { value: "Nor-Este", label: "Nor-Este" },
  { value: "Nor-Oeste", label: "Nor-Oeste" }
];

const FichaCatastral = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  const [columnFilters, setColumnFilters] = useState({
    nro_ficha: "", codigo_catastral: "", rif: "", propietario: "", categoria_inmueble: "", fecha_inscripcion: ""
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [initialEditLoad, setInitialEditLoad] = useState(false);

  // Contribuyentes
  const [contribuyentes, setContribuyentes] = useState([]);
  const [propietarios, setPropietarios] = useState([{ clase_contribuyente: "", rif: "", contribuyente_id: "", nombre_razon_social: "" }]);
  const [propietarioPopoversOpen, setPropietarioPopoversOpen] = useState([false]);

  // Filtros
  // Validación
  const [codigoCatastralError, setCodigoCatastralError] = useState("");

  // Linderos
  const [linderoOpen, setLinderoOpen] = useState({ 1: false, 2: false, 3: false, 4: false });
  const [linderoBusqueda, setLinderoBusqueda] = useState("");

  // Tablas de referencia
  const [opcionesClasificadorInmuebles, setOpcionesClasificadorInmuebles] = useState([]);
  const [opcionesTablaPVT, setOpcionesTablaPVT] = useState([]);
  const [datosTablaPVT, setDatosTablaPVT] = useState([]);
  const [opcionesTablaTVC, setOpcionesTablaTVC] = useState([]);
  const [datosTablaTVC, setDatosTablaTVC] = useState([]);
  const [tablaTVCOpen, setTablaTVCOpen] = useState(false);
  const [tablaTVCBusqueda, setTablaTVCBusqueda] = useState("");
  const [datosTablaDepreciacion, setDatosTablaDepreciacion] = useState([]);
  const [cotizacionActiva, setCotizacionActiva] = useState(null);

  // ==================== VALIDACIÓN ====================

  const validarCodigoCatastral = (codigo) => {
    if (!codigo) return true;
    return /^\d-\d{6}$/.test(codigo);
  };

  const verificarCodigoCatastralUnico = async (codigo, currentId = null) => {
    if (!codigo) return true;
    try {
      const response = await axios.get(`${API}/api/catastro/ficha-catastral`, { headers });
      const existe = response.data.some(f => f.codigo_catastral === codigo && f.id !== currentId);
      return !existe;
    } catch (error) {
      console.error("Error verificando código catastral:", error);
      return true;
    }
  };

  // ==================== FETCH FUNCTIONS ====================

  const fetchData = async (page = currentPage, filters = columnFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize });
      if (filters.nro_ficha) params.append("f_nro_ficha", filters.nro_ficha);
      if (filters.codigo_catastral) params.append("f_codigo", filters.codigo_catastral);
      if (filters.rif) params.append("f_rif", filters.rif);
      if (filters.propietario) params.append("f_propietario", filters.propietario);
      if (filters.categoria_inmueble) params.append("f_categoria", filters.categoria_inmueble);
      if (filters.fecha_inscripcion) params.append("f_fecha", filters.fecha_inscripcion);

      const response = await axios.get(`${API}/api/catastro/ficha-catastral?${params.toString()}`, { headers });
      setData(response.data.data || []);
      setTotalRecords(response.data.total || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  // Debounce column filters
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(0, columnFilters);
    }, 400);
    return () => clearTimeout(timer);
  }, [columnFilters]);

  useEffect(() => { fetchData(0); }, [token]);

  const fetchContribuyentes = async (clase) => {
    try {
      const claseParam = clase === "Contribuyente con LAE" ? "con-lae" : "sin-lae";
      const response = await axios.get(`${API}/api/contribuyentes/buscar?clase=${claseParam}`, { headers });
      setContribuyentes(response.data);
    } catch (error) {
      console.error("Error al cargar contribuyentes");
    }
  };

  const fetchOpcionesTablaPVT = async () => {
    try {
      const response = await axios.get(`${API}/api/catastro/tabla-pvt`, { headers });
      setDatosTablaPVT(response.data);
      const clasificaciones = [...new Set(response.data.map(item => item.clasificacion_terreno).filter(c => c && c.trim() !== ""))];
      setOpcionesTablaPVT(clasificaciones);
    } catch (error) {
      console.error("Error al cargar opciones de Tabla PVT:", error);
    }
  };

  const obtenerTCMMVPorClasificacion = (clasificacion) => {
    const registro = datosTablaPVT.find(item => item.clasificacion_terreno === clasificacion);
    return registro?.tcmmv_m2 || 0;
  };

  const fetchOpcionesTablaTVC = async () => {
    try {
      const response = await axios.get(`${API}/api/catastro/tabla-tvc`, { headers });
      setDatosTablaTVC(response.data);
      const opciones = response.data.map(item => ({
        id: item.id,
        valor: `${item.tipologia} - ${item.descripcion}`,
        tipologia: item.tipologia,
        descripcion: item.descripcion
      }));
      setOpcionesTablaTVC(opciones);
    } catch (error) {
      console.error("Error al cargar opciones de Tabla TVC:", error);
    }
  };

  const fetchTablaDepreciacion = async () => {
    try {
      const response = await axios.get(`${API}/api/catastro/tabla-depreciacion`, { headers });
      setDatosTablaDepreciacion(response.data);
    } catch (error) {
      console.error("Error al cargar Tabla Depreciación:", error);
    }
  };

  const fetchCotizacionActiva = async () => {
    try {
      const response = await axios.get(`${API}/api/definiciones/cotizaciones`, { headers });
      const activa = response.data.find(c => c.estatus === "Activo");
      setCotizacionActiva(activa);
    } catch (error) {
      console.error("Error al cargar cotización activa:", error);
    }
  };

  const fetchClasificadorInmuebles = async () => {
    try {
      const response = await axios.get(`${API}/api/catastro/clasificador-inmuebles`, { headers });
      setOpcionesClasificadorInmuebles(response.data || []);
    } catch (err) {
      console.log("Error al cargar clasificador inmuebles:", err);
    }
  };

  // ==================== CÁLCULOS ====================

  const calcularDepreciacion = (anioInscripcion) => {
    if (!anioInscripcion || datosTablaDepreciacion.length === 0) {
      return { tablaDepreciacion: "", porcentajeDepreciacion: 0 };
    }
    const antiguedad = new Date().getFullYear() - anioInscripcion;
    const rangoEncontrado = datosTablaDepreciacion.find(item => {
      const desde = item.anios_antiguedad || 0;
      const hasta = item.hasta_anios_antiguedad || 999;
      return antiguedad >= desde && antiguedad <= hasta;
    });
    if (rangoEncontrado) {
      return {
        tablaDepreciacion: rangoEncontrado.descripcion || "",
        porcentajeDepreciacion: rangoEncontrado.porcentaje_depreciacion || 0
      };
    }
    return { tablaDepreciacion: "", porcentajeDepreciacion: 0 };
  };

  const obtenerValorTipologia = (tablaTVC, zona) => {
    if (!tablaTVC || !zona) return 0;
    const registro = datosTablaTVC.find(item => `${item.tipologia} - ${item.descripcion}` === tablaTVC);
    if (!registro) return 0;
    const mapaZonas = { 'A': registro.valor_a, 'B': registro.valor_b, 'C': registro.valor_c, 'D': registro.valor_d, 'E': registro.valor_e, 'F': registro.valor_f };
    return mapaZonas[zona] || 0;
  };

  // Para Avalúo 2 - busca por tipología parcial
  const obtenerValorTipologiaPorZona = (tipologia, zona) => {
    if (!tipologia || !zona) return 0;
    const registro = datosTablaTVC.find(item => item.tipologia === tipologia);
    if (!registro) return 0;
    const mapaZonas = { 'A': registro.valor_a, 'B': registro.valor_b, 'C': registro.valor_c, 'D': registro.valor_d, 'E': registro.valor_e, 'F': registro.valor_f };
    return mapaZonas[zona] || 0;
  };

  // ==================== USE EFFECTS ====================

  // Cargar contribuyentes cuando cambia la clase en propietarios
  useEffect(() => {
    if (propietarios.length > 0) {
      const clasesSeleccionadas = propietarios.map(p => p.clase_contribuyente).filter(c => c);
      if (clasesSeleccionadas.length > 0) {
        const cargarTodos = async () => {
          try {
            let todosContribuyentes = [];
            const tieneConLAE = clasesSeleccionadas.includes("Contribuyente con LAE");
            const tieneSinLAE = clasesSeleccionadas.includes("Contribuyente sin LAE");
            if (tieneConLAE) {
              const r = await axios.get(`${API}/api/contribuyentes/buscar?clase=con-lae`, { headers });
              todosContribuyentes = [...todosContribuyentes, ...r.data];
            }
            if (tieneSinLAE) {
              const r = await axios.get(`${API}/api/contribuyentes/buscar?clase=sin-lae`, { headers });
              todosContribuyentes = [...todosContribuyentes, ...r.data];
            }
            setContribuyentes(todosContribuyentes);
          } catch (error) {
            console.error("Error al cargar contribuyentes:", error);
          }
        };
        cargarTodos();
      }
    }
  }, [propietarios]);

  // Cargar tablas de referencia al abrir el diálogo
  useEffect(() => {
    if (dialogOpen) {
      fetchOpcionesTablaPVT();
      fetchOpcionesTablaTVC();
      fetchCotizacionActiva();
      fetchTablaDepreciacion();
      fetchClasificadorInmuebles();
    }
  }, [dialogOpen]);

  // Depreciación automática
  useEffect(() => {
    if (formData.anio_inscripcion_inmueble && datosTablaDepreciacion.length > 0) {
      const { tablaDepreciacion, porcentajeDepreciacion } = calcularDepreciacion(formData.anio_inscripcion_inmueble);
      if (tablaDepreciacion !== formData.tabla_depreciacion || porcentajeDepreciacion !== formData.depreciacion_porcentaje) {
        setFormData(prev => ({ ...prev, tabla_depreciacion: tablaDepreciacion, depreciacion_porcentaje: porcentajeDepreciacion }));
      }
    }
  }, [formData.anio_inscripcion_inmueble, datosTablaDepreciacion]);

  // Año de inscripción automático desde fecha de registro
  useEffect(() => {
    let fechaRegistro = null;
    if (formData.tipo_registro === "Actual" && formData.fecha_registro) {
      fechaRegistro = formData.fecha_registro;
    } else if (formData.tipo_registro === "Anterior" && formData.fecha_registro_actual) {
      fechaRegistro = formData.fecha_registro_actual;
    }
    if (fechaRegistro) {
      const anio = new Date(fechaRegistro).getFullYear();
      if (anio && anio !== formData.anio_inscripcion_inmueble) {
        setFormData(prev => ({ ...prev, anio_inscripcion_inmueble: anio }));
      }
    }
  }, [formData.fecha_registro, formData.fecha_registro_actual, formData.tipo_registro]);

  // Valor Catastral Terreno (M.E.) = Área × Valor Zona
  useEffect(() => {
    const valorZona = parseFloat(formData.valor_zona) || 0;
    const area = parseFloat(formData.area_terreno_m2) || 0;
    const val = area * valorZona;
    if (val !== formData.valor_catastral_terreno_me) {
      setFormData(prev => ({ ...prev, valor_catastral_terreno_me: val }));
    }
  }, [formData.valor_zona, formData.area_terreno_m2]);

  // Valor Catastral Terreno (Bs.) = ME × Unidad de Cuenta
  useEffect(() => {
    const me = parseFloat(formData.valor_catastral_terreno_me) || 0;
    const uc = cotizacionActiva?.valor_unidad_cuenta || 0;
    const val = me * uc;
    if (val !== formData.valor_catastral_terreno_bs) {
      setFormData(prev => ({ ...prev, valor_catastral_terreno_bs: val }));
    }
  }, [formData.valor_catastral_terreno_me, cotizacionActiva]);

  // Valor Catastral Construcción (M.E.) = (Área × Tipología) - Depreciación%
  useEffect(() => {
    const area = parseFloat(formData.area_construccion_m2) || 0;
    const tip = parseFloat(formData.valor_tipologia) || 0;
    const dep = parseFloat(formData.depreciacion_porcentaje) || 0;
    const base = area * tip;
    const val = base - (base * dep / 100);
    if (val !== formData.valor_catastral_construccion_me) {
      setFormData(prev => ({ ...prev, valor_catastral_construccion_me: val }));
    }
  }, [formData.area_construccion_m2, formData.valor_tipologia, formData.depreciacion_porcentaje]);

  // Valor Catastral Construcción (Bs.) = ME × Unidad de Cuenta
  useEffect(() => {
    const me = parseFloat(formData.valor_catastral_construccion_me) || 0;
    const uc = cotizacionActiva?.valor_unidad_cuenta || 0;
    const val = me * uc;
    if (val !== formData.valor_catastral_construccion_bs) {
      setFormData(prev => ({ ...prev, valor_catastral_construccion_bs: val }));
    }
  }, [formData.valor_catastral_construccion_me, cotizacionActiva]);

  // Valor Catastral Inmueble (M.E.) = Terreno ME + Construcción ME
  useEffect(() => {
    const t = parseFloat(formData.valor_catastral_terreno_me) || 0;
    const c = parseFloat(formData.valor_catastral_construccion_me) || 0;
    const val = t + c;
    if (val !== formData.valor_catastral_inmueble_me) {
      setFormData(prev => ({ ...prev, valor_catastral_inmueble_me: val }));
    }
  }, [formData.valor_catastral_terreno_me, formData.valor_catastral_construccion_me]);

  // Valor Catastral Inmueble (Bs.) = ME × Unidad de Cuenta
  useEffect(() => {
    const me = parseFloat(formData.valor_catastral_inmueble_me) || 0;
    const uc = cotizacionActiva?.valor_unidad_cuenta || 0;
    const val = me * uc;
    if (val !== formData.valor_catastral_inmueble_bs) {
      setFormData(prev => ({ ...prev, valor_catastral_inmueble_bs: val }));
    }
  }, [formData.valor_catastral_inmueble_me, cotizacionActiva]);

  // ============ CÁLCULOS AVALÚO 2 (CLASIFICADOR MIXTO) ============

  useEffect(() => {
    if (formData.clasificador_mixto && formData.anio_inscripcion_inmueble_2 && datosTablaDepreciacion.length > 0) {
      const { tablaDepreciacion, porcentajeDepreciacion } = calcularDepreciacion(formData.anio_inscripcion_inmueble_2);
      if (tablaDepreciacion !== formData.tabla_depreciacion_2 || porcentajeDepreciacion !== formData.depreciacion_porcentaje_2) {
        setFormData(prev => ({ ...prev, tabla_depreciacion_2: tablaDepreciacion, depreciacion_porcentaje_2: porcentajeDepreciacion }));
      }
    }
  }, [formData.anio_inscripcion_inmueble_2, formData.clasificador_mixto, datosTablaDepreciacion]);

  useEffect(() => {
    if (formData.clasificador_mixto) {
      const v2 = parseFloat(formData.valor_zona_2) || 0;
      const a2 = parseFloat(formData.area_terreno_m2_2) || 0;
      const val = a2 * v2;
      if (val !== formData.valor_catastral_terreno_me_2) {
        setFormData(prev => ({ ...prev, valor_catastral_terreno_me_2: val }));
      }
    }
  }, [formData.valor_zona_2, formData.area_terreno_m2_2, formData.clasificador_mixto]);

  useEffect(() => {
    if (formData.clasificador_mixto) {
      const me2 = parseFloat(formData.valor_catastral_terreno_me_2) || 0;
      const uc = cotizacionActiva?.valor_unidad_cuenta || 0;
      const val = me2 * uc;
      if (val !== formData.valor_catastral_terreno_bs_2) {
        setFormData(prev => ({ ...prev, valor_catastral_terreno_bs_2: val }));
      }
    }
  }, [formData.valor_catastral_terreno_me_2, formData.clasificador_mixto, cotizacionActiva]);

  useEffect(() => {
    if (formData.clasificador_mixto) {
      const a2 = parseFloat(formData.area_construccion_m2_2) || 0;
      const t2 = parseFloat(formData.valor_tipologia_2) || 0;
      const d2 = parseFloat(formData.depreciacion_porcentaje_2) || 0;
      const base = a2 * t2;
      const val = base - (base * d2 / 100);
      if (val !== formData.valor_catastral_construccion_me_2) {
        setFormData(prev => ({ ...prev, valor_catastral_construccion_me_2: val }));
      }
    }
  }, [formData.area_construccion_m2_2, formData.valor_tipologia_2, formData.depreciacion_porcentaje_2, formData.clasificador_mixto]);

  useEffect(() => {
    if (formData.clasificador_mixto) {
      const me2 = parseFloat(formData.valor_catastral_construccion_me_2) || 0;
      const uc = cotizacionActiva?.valor_unidad_cuenta || 0;
      const val = me2 * uc;
      if (val !== formData.valor_catastral_construccion_bs_2) {
        setFormData(prev => ({ ...prev, valor_catastral_construccion_bs_2: val }));
      }
    }
  }, [formData.valor_catastral_construccion_me_2, formData.clasificador_mixto, cotizacionActiva]);

  useEffect(() => {
    if (formData.clasificador_mixto) {
      const t2 = parseFloat(formData.valor_catastral_terreno_me_2) || 0;
      const c2 = parseFloat(formData.valor_catastral_construccion_me_2) || 0;
      const val = t2 + c2;
      if (val !== formData.valor_catastral_inmueble_me_2) {
        setFormData(prev => ({ ...prev, valor_catastral_inmueble_me_2: val }));
      }
    }
  }, [formData.valor_catastral_terreno_me_2, formData.valor_catastral_construccion_me_2, formData.clasificador_mixto]);

  useEffect(() => {
    if (formData.clasificador_mixto) {
      const me2 = parseFloat(formData.valor_catastral_inmueble_me_2) || 0;
      const uc = cotizacionActiva?.valor_unidad_cuenta || 0;
      const val = me2 * uc;
      if (val !== formData.valor_catastral_inmueble_bs_2) {
        setFormData(prev => ({ ...prev, valor_catastral_inmueble_bs_2: val }));
      }
    }
  }, [formData.valor_catastral_inmueble_me_2, formData.clasificador_mixto, cotizacionActiva]);

  // ==================== CRUD ====================

  const handleCreate = async () => {
    if (!formData.codigo_catastral) { toast.error("El Código Catastral es obligatorio"); return; }
    if (!validarCodigoCatastral(formData.codigo_catastral)) { toast.error("Formato de Código Catastral inválido. Use: X-XXXXXX (ej: 1-000001)"); return; }
    const esUnico = await verificarCodigoCatastralUnico(formData.codigo_catastral);
    if (!esUnico) { toast.error("El Código Catastral ya existe. Debe ser único."); setCodigoCatastralError("Este código ya está registrado"); return; }
    const propietariosValidos = propietarios.filter(p => p.rif);
    if (propietariosValidos.length === 0) { toast.error("Debe seleccionar al menos un propietario"); return; }

    try {
      let dataToSend = { ...formData };
      dataToSend.propietarios = propietariosValidos;
      if (propietarios.length > 0 && propietarios[0].rif) {
        dataToSend.clase_contribuyente = propietarios[0].clase_contribuyente;
        dataToSend.rif = propietarios[0].rif;
        dataToSend.contribuyente_id = propietarios[0].contribuyente_id;
        dataToSend.nombre_razon_social = propietarios[0].nombre_razon_social;
      }
      await axios.post(`${API}/api/catastro/ficha-catastral`, dataToSend, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      setCodigoCatastralError("");
      setPropietarios([{ clase_contribuyente: "", rif: "", contribuyente_id: "", nombre_razon_social: "" }]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    if (!formData.codigo_catastral) { toast.error("El Código Catastral es obligatorio"); return; }
    if (!validarCodigoCatastral(formData.codigo_catastral)) { toast.error("Formato de Código Catastral inválido. Use: X-XXXXXX (ej: 1-000001)"); return; }
    const esUnico = await verificarCodigoCatastralUnico(formData.codigo_catastral, editingItem?.id);
    if (!esUnico) { toast.error("El Código Catastral ya existe. Debe ser único."); setCodigoCatastralError("Este código ya está registrado"); return; }
    const propietariosValidos = propietarios.filter(p => p.rif);
    if (propietariosValidos.length === 0) { toast.error("Debe seleccionar al menos un propietario"); return; }

    try {
      let dataToSend = { ...formData };
      dataToSend.propietarios = propietariosValidos;
      if (propietarios.length > 0 && propietarios[0].rif) {
        dataToSend.clase_contribuyente = propietarios[0].clase_contribuyente;
        dataToSend.rif = propietarios[0].rif;
        dataToSend.contribuyente_id = propietarios[0].contribuyente_id;
        dataToSend.nombre_razon_social = propietarios[0].nombre_razon_social;
      }
      await axios.put(`${API}/api/catastro/ficha-catastral/${editingItem.id}`, dataToSend, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      setCodigoCatastralError("");
      setPropietarios([{ clase_contribuyente: "", rif: "", contribuyente_id: "", nombre_razon_social: "" }]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/api/catastro/ficha-catastral/${id}`, { headers });
      toast.success("Registro eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    }
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    const today = new Date().toISOString().split('T')[0];
    setPropietarios([{ clase_contribuyente: "", rif: "", contribuyente_id: "", nombre_razon_social: "" }]);
    setPropietarioPopoversOpen([false]);
    setFormData({ fecha_inscripcion: today, tipo_registro: "", exoneracion: "NO" });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setInitialEditLoad(true);
    setFormData(item);
    if (item.propietarios && item.propietarios.length > 0) {
      setPropietarios(item.propietarios);
      setPropietarioPopoversOpen(item.propietarios.map(() => false));
    } else if (item.rif) {
      setPropietarios([{ clase_contribuyente: item.clase_contribuyente || "", rif: item.rif || "", contribuyente_id: item.contribuyente_id || "", nombre_razon_social: item.nombre_razon_social || "" }]);
      setPropietarioPopoversOpen([false]);
    } else {
      setPropietarios([{ clase_contribuyente: "", rif: "", contribuyente_id: "", nombre_razon_social: "" }]);
      setPropietarioPopoversOpen([false]);
    }
    if (item.clase_contribuyente) fetchContribuyentes(item.clase_contribuyente);
    setDialogOpen(true);
  };

  // ==================== EXPORTACIONES ====================

  const exportFichaToExcel = () => {
    const exportData = data.map(item => ({
      'Nro. Ficha': item.nro_ficha, 'Código Catastral': item.codigo_catastral, 'RIF': item.rif,
      'Propietario': item.nombre_razon_social, 'Clasificador Inmueble': item.categoria_inmueble,
      'Dirección': item.direccion, 'Sector': item.sector, 'Manzana': item.manzana, 'Parcela': item.parcela,
      'Área Terreno (m²)': item.area_terreno, 'Área Construcción (m²)': item.area_construccion,
      'Valor Terreno': item.valor_terreno, 'Valor Construcción': item.valor_construccion,
      'Valor Total': item.valor_total, 'Fecha Inscripción': item.fecha_inscripcion,
      'Observaciones': item.observaciones || '-'
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fichas Catastrales');
    XLSX.writeFile(wb, `fichas_catastrales_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportFichaToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(16);
    doc.text('Fichas Catastrales', 14, 15);
    doc.setFontSize(10);
    doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-VE')}`, 14, 22);
    const tableData = data.map(item => [
      item.nro_ficha, item.codigo_catastral, item.rif, item.nombre_razon_social || '-',
      item.categoria_inmueble || '-', item.area_terreno || '-', item.area_construccion || '-', item.fecha_inscripcion || '-'
    ]);
    autoTable(doc, {
      head: [['Nro. Ficha', 'Cód. Catastral', 'RIF', 'Propietario', 'Categoría Inmueble', 'Área Terreno', 'Área Const.', 'Fecha Inscripción']],
      body: tableData, startY: 28, styles: { fontSize: 8 }, headStyles: { fillColor: [59, 130, 246] }
    });
    doc.save(`fichas_catastrales_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportFichaToHTML = () => {
    const hdrs = ['Nro. Ficha', 'Código Catastral', 'RIF', 'Propietario', 'Categoría Inmueble', 'Área Terreno', 'Área Construcción', 'Fecha Inscripción'];
    let html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Fichas Catastrales</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#1e40af}table{border-collapse:collapse;width:100%;margin-top:20px}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background-color:#3b82f6;color:white}tr:nth-child(even){background-color:#f2f2f2}</style></head><body><h1>Fichas Catastrales</h1><p>Fecha de generación: ${new Date().toLocaleDateString('es-VE')}</p><table><thead><tr>${hdrs.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`;
    data.forEach(item => {
      html += `<tr><td>${item.nro_ficha}</td><td>${item.codigo_catastral || '-'}</td><td>${item.rif || '-'}</td><td>${item.nombre_razon_social || '-'}</td><td>${item.categoria_inmueble || '-'}</td><td>${item.area_terreno || '-'}</td><td>${item.area_construccion || '-'}</td><td>${item.fecha_inscripcion || '-'}</td></tr>`;
    });
    html += `</tbody></table></body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    saveAs(blob, `fichas_catastrales_${new Date().toISOString().split('T')[0]}.html`);
  };

  const exportFichaToCSV = () => {
    const hdrs = ['Nro. Ficha', 'Código Catastral', 'RIF', 'Propietario', 'Categoría Inmueble', 'Dirección', 'Área Terreno', 'Área Construcción', 'Fecha Inscripción'];
    let csv = hdrs.join(',') + '\n';
    data.forEach(item => {
      csv += [item.nro_ficha, item.codigo_catastral || '', item.rif || '', `"${(item.nombre_razon_social || '').replace(/"/g, '""')}"`, item.categoria_inmueble || '', `"${(item.direccion || '').replace(/"/g, '""')}"`, item.area_terreno || '', item.area_construccion || '', item.fecha_inscripcion || ''].join(',') + '\n';
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `fichas_catastrales_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const exportFichaToText = () => {
    let text = 'FICHAS CATASTRALES\n';
    text += `Fecha de generación: ${new Date().toLocaleDateString('es-VE')}\n`;
    text += '='.repeat(100) + '\n\n';
    data.forEach((item, index) => {
      text += `Registro #${index + 1}\n` + '-'.repeat(50) + '\n';
      text += `Nro. Ficha: ${item.nro_ficha}\nCódigo Catastral: ${item.codigo_catastral || '-'}\nRIF: ${item.rif || '-'}\nPropietario: ${item.nombre_razon_social || '-'}\nClasificador Inmueble: ${item.categoria_inmueble || '-'}\nDirección: ${item.direccion || '-'}\nSector: ${item.sector || '-'}\nManzana: ${item.manzana || '-'}\nParcela: ${item.parcela || '-'}\nÁrea Terreno: ${item.area_terreno || '-'} m²\nÁrea Construcción: ${item.area_construccion || '-'} m²\nValor Terreno: ${item.valor_terreno || '-'}\nValor Construcción: ${item.valor_construccion || '-'}\nValor Total: ${item.valor_total || '-'}\nFecha Inscripción: ${item.fecha_inscripcion || '-'}\nObservaciones: ${item.observaciones || '-'}\n\n`;
    });
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `fichas_catastrales_${new Date().toISOString().split('T')[0]}.txt`);
  };

  const exportFichaToJSON = () => {
    const exportData = data.map(item => ({
      nro_ficha: item.nro_ficha, codigo_catastral: item.codigo_catastral, rif: item.rif,
      propietario: item.nombre_razon_social, categoria_inmueble: item.categoria_inmueble,
      direccion: item.direccion, sector: item.sector, manzana: item.manzana, parcela: item.parcela,
      area_terreno: item.area_terreno, area_construccion: item.area_construccion,
      valor_terreno: item.valor_terreno, valor_construccion: item.valor_construccion,
      valor_total: item.valor_total, fecha_inscripcion: item.fecha_inscripcion, observaciones: item.observaciones
    }));
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    saveAs(blob, `fichas_catastrales_${new Date().toISOString().split('T')[0]}.json`);
  };

  // ==================== IMPRIMIR ====================

  const handlePrintFichaCatastral = async (item) => {
    let configData = {};
    try {
      const response = await axios.get(`${API}/api/definiciones/configuracion/actual`, { headers });
      configData = response.data;
    } catch (error) {
      console.error("Error obteniendo configuración:", error);
    }
    const printWindow = window.open('', '_blank');
    const qrData = JSON.stringify({ ficha: item.nro_ficha, codigo: item.codigo_catastral, rif: item.rif });
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}`;
    const propietariosHTML = item.propietarios && item.propietarios.length > 0 
      ? item.propietarios.map((p, idx) => `<div class="propietario-item"><span class="propietario-num">${idx + 1}.</span><span class="propietario-rif">${p.rif || 'N/A'}</span> - <span class="propietario-nombre">${p.nombre_razon_social || 'N/A'}</span><span class="propietario-clase">(${p.clase_contribuyente || 'N/A'})</span></div>`).join('')
      : `<div class="propietario-item"><span class="propietario-rif">${item.rif || 'N/A'}</span> - <span class="propietario-nombre">${item.nombre_razon_social || 'N/A'}</span></div>`;

    const printContent = `<!DOCTYPE html><html><head><title>Ficha Catastral - ${item.nro_ficha}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:15px;font-size:11px}.header-container{display:flex;justify-content:space-between;align-items:center;margin-bottom:15px;border-bottom:2px solid #333;padding-bottom:15px}.logo-container{width:80px;height:80px}.logo-container img{max-width:100%;max-height:100%;object-fit:contain}.header-text{flex:1;text-align:center;padding:0 15px}.header-text h1{font-size:12px;font-weight:bold;margin-bottom:3px}.header-text h2{font-size:11px;font-weight:bold;margin-bottom:2px}.header-text h3{font-size:10px;margin-bottom:2px}.header-text h4{font-size:13px;font-weight:bold;margin-top:5px;text-decoration:underline}.ficha-number{background:#333;color:white;padding:8px 15px;font-size:14px;font-weight:bold;text-align:center;margin-bottom:15px}.section{margin-bottom:12px}.section h3{background:#e8e8e8;padding:6px 8px;margin-bottom:8px;font-size:11px;border-left:3px solid #333}.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:5px;padding:0 8px}.info-grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;padding:0 8px}.info-item{display:flex;font-size:10px;margin-bottom:3px}.info-label{font-weight:bold;min-width:140px;color:#444}.info-value{color:#000}.propietarios-list{padding:0 8px}.propietario-item{padding:4px 0;border-bottom:1px solid #eee;font-size:10px}.propietario-num{font-weight:bold;color:#333;margin-right:5px}.propietario-rif{font-weight:bold}.propietario-clase{color:#666;font-style:italic;margin-left:5px}.qr-section{display:flex;justify-content:space-between;align-items:flex-start;margin-top:20px;padding-top:15px;border-top:1px solid #ccc}.qr-container{text-align:center}.qr-container img{width:80px;height:80px}.qr-container p{font-size:8px;color:#666;margin-top:3px}.signature-area{display:flex;justify-content:space-around;flex:1}.signature-box{text-align:center;width:180px}.signature-line{border-top:1px solid #333;margin-top:45px;padding-top:4px;font-size:9px}.director-catastro{position:relative}.director-catastro .firma-img{position:absolute;bottom:30px;left:50%;transform:translateX(-50%);max-width:120px;max-height:50px;object-fit:contain}.director-catastro .signature-line{margin-top:55px}.director-nombre{font-weight:normal;font-size:8px;color:#333}.nota-legal{margin-top:20px;padding:10px;background:#f9f9f9;border:1px solid #ddd;font-size:8px;text-align:justify;line-height:1.4}.footer{margin-top:15px;text-align:center;font-size:8px;color:#777;border-top:1px solid #ccc;padding-top:8px}@media print{body{padding:10px}}</style></head><body>
      <div class="header-container">
        <div class="logo-container">${configData.logo_encabezado_izquierdo ? `<img src="${configData.logo_encabezado_izquierdo}" alt="Escudo" />` : '<div style="width:80px;height:80px;border:1px dashed #ccc;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">Escudo</div>'}</div>
        <div class="header-text"><h1>REPUBLICA BOLIVARIANA DE VENEZUELA</h1><h2>${configData.nombre_alcaldia || 'ALCALDIA DEL MUNICIPIO'}</h2><h3>${configData.nombre_superintendencia || 'DIRECCION DE CATASTRO'}</h3><h4>FICHA CATASTRAL</h4></div>
        <div class="logo-container">${configData.logo_encabezado_derecho ? `<img src="${configData.logo_encabezado_derecho}" alt="Logo Institucional" />` : '<div style="width:80px;height:80px;border:1px dashed #ccc;display:flex;align-items:center;justify-content:center;font-size:8px;color:#999;">Logo</div>'}</div>
      </div>
      <div class="ficha-number">FICHA Nro. ${item.nro_ficha || 'N/A'} | CODIGO CATASTRAL: ${item.codigo_catastral || 'N/A'}</div>
      <div class="section"><h3>DATOS DEL INMUEBLE</h3><div class="info-grid">
        <div class="info-item"><span class="info-label">Categoria Inmueble:</span><span class="info-value">${item.categoria_inmueble || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Tipo de Registro:</span><span class="info-value">${item.tipo_registro || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Fecha Inscripcion:</span><span class="info-value">${item.fecha_inscripcion || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Sector:</span><span class="info-value">${item.sector || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Direccion:</span><span class="info-value">${item.direccion_inmueble || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Lindero Norte:</span><span class="info-value">${item.lindero_norte || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Lindero Sur:</span><span class="info-value">${item.lindero_sur || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Lindero Este:</span><span class="info-value">${item.lindero_este || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Lindero Oeste:</span><span class="info-value">${item.lindero_oeste || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Area Terreno (m2):</span><span class="info-value">${item.area_terreno_m2 || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Area Construccion (m2):</span><span class="info-value">${item.area_construccion_m2 || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Uso Inmueble:</span><span class="info-value">${item.uso_inmueble || 'N/A'}</span></div>
      </div></div>
      <div class="section"><h3>PROPIETARIO(S)</h3><div class="propietarios-list">${propietariosHTML}</div></div>
      <div class="section"><h3>DATOS REGISTRALES</h3><div class="info-grid-3">
        <div class="info-item"><span class="info-label">Nro. Registro:</span><span class="info-value">${item.nro_registro || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Tomo:</span><span class="info-value">${item.tomo || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Folio:</span><span class="info-value">${item.folio || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Protocolo:</span><span class="info-value">${item.protocolo || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Fecha Documento:</span><span class="info-value">${item.fecha_documento || 'N/A'}</span></div>
        <div class="info-item"><span class="info-label">Exoneracion:</span><span class="info-value">${item.exoneracion || 'NO'}</span></div>
      </div></div>
      <div class="qr-section">
        <div class="qr-container"><img src="${qrImageUrl}" alt="Codigo QR" /><p>Codigo de verificacion</p></div>
        <div class="signature-area">
          <div class="signature-box"><div class="signature-line">Firma del Propietario</div></div>
          <div class="signature-box director-catastro">
            ${configData.firma_director_catastro && configData.firma_director_catastro.startsWith('data:image') ? `<img src="${configData.firma_director_catastro}" alt="Firma Director Catastro" class="firma-img" />` : ''}
            <div class="signature-line"><strong>DIRECTOR(A) DE CATASTRO</strong>${configData.nombre_director_catastro ? `<br/><span class="director-nombre">${configData.nombre_director_catastro}</span>` : ''}</div>
          </div>
        </div>
      </div>
      <div class="nota-legal"><strong>Nota:</strong> Todos los montos expresados en este documento son calculados de acuerdo a lo establecido en la Ley Organica de Coordinacion y Armonizacion de las Potestades Tributarias de los Estados y Municipios (LOCAPTEM), la Resolucion N 011-2023 publicada en gaceta oficial N 6.783 de fecha 29 de diciembre de 2023 y las ordenanzas municipales correspondientes en materia tributaria.</div>
      <div class="footer"><p>Documento generado el ${new Date().toLocaleString('es-VE')} | Sistema SIRET - Sistema de Recaudacion Tributaria Municipal</p></div>
    </body></html>`;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  // ==================== FILTRADO (ahora del servidor) ====================
  // Los filtros de columna ahora se usan como búsqueda combinada del servidor
  const totalPages = Math.ceil(totalRecords / pageSize);

  const hasActiveFilters = Object.values(columnFilters).some(v => v !== "");

  const PaginationControls = () => (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-500">
        Pag. {currentPage + 1} de {totalPages || 1}
      </span>
      <Button variant="outline" size="sm" onClick={() => fetchData(currentPage - 1)} disabled={currentPage === 0 || loading} data-testid="btn-prev-page">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <Button variant="outline" size="sm" onClick={() => fetchData(currentPage + 1)} disabled={currentPage >= totalPages - 1 || loading} data-testid="btn-next-page">
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );

  // ==================== FORMULARIO ====================

  const renderFichaCatastralForm = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Datos Básicos */}
      <div className="md:col-span-3 border-b pb-2 mb-2">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold text-slate-700">Datos del Contribuyente / Propietario(s)</h3>
          <Button type="button" variant="outline" size="sm" onClick={() => { setPropietarios([...propietarios, { clase_contribuyente: "", rif: "", contribuyente_id: "", nombre_razon_social: "" }]); setPropietarioPopoversOpen([...propietarioPopoversOpen, false]); }} className="text-blue-600 border-blue-300 hover:bg-blue-50" data-testid="btn-agregar-propietario">
            <Plus className="w-4 h-4 mr-1" /> Propietario
          </Button>
        </div>
      </div>

      {propietarios.map((propietario, index) => (
        <React.Fragment key={index}>
          <div className="space-y-2">
            <Label>Clase de Contribuyente {index > 0 && `(${index + 1})`}</Label>
            <Select value={propietario.clase_contribuyente || ""} onValueChange={(v) => { const n = [...propietarios]; n[index] = { ...n[index], clase_contribuyente: v, rif: "", contribuyente_id: "", nombre_razon_social: "" }; setPropietarios(n); }}>
              <SelectTrigger data-testid={`form-clase-${index}`}><SelectValue placeholder="Seleccione clase" /></SelectTrigger>
              <SelectContent>{clasesContribuyente.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>RIF / Contribuyente {index > 0 && `(${index + 1})`}</Label>
            <div className="flex gap-2">
              <Popover open={propietarioPopoversOpen[index]} onOpenChange={(open) => { const n = [...propietarioPopoversOpen]; n[index] = open; setPropietarioPopoversOpen(n); }}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-10 font-normal" disabled={!propietario.clase_contribuyente} data-testid={`form-rif-${index}`}>
                    {propietario.rif ? <span className="truncate">{propietario.nombre_razon_social ? `${propietario.rif} - ${propietario.nombre_razon_social}` : propietario.rif}</span> : <span className="text-slate-500">{propietario.clase_contribuyente ? "Seleccione..." : "Primero seleccione clase"}</span>}
                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar..." />
                    <CommandList><CommandEmpty>No encontrado</CommandEmpty>
                      <CommandGroup>
                        {contribuyentes.filter(c => { if (propietario.clase_contribuyente === "Contribuyente con LAE") return c.clase === "con-lae"; if (propietario.clase_contribuyente === "Contribuyente sin LAE") return c.clase === "sin-lae"; return true; }).map(c => (
                          <CommandItem key={c.id} value={c.display_name} onSelect={() => {
                            const np = [...propietarios];
                            np[index] = { ...np[index], rif: `${c.tipo_rif}-${c.rif}`, contribuyente_id: c.id, nombre_razon_social: c.nombre || c.razon_social };
                            setPropietarios(np);
                            if (index === 0) setFormData({...formData, rif: `${c.tipo_rif}-${c.rif}`, contribuyente_id: c.id, nombre_razon_social: c.nombre || c.razon_social, clase_contribuyente: propietario.clase_contribuyente});
                            const npp = [...propietarioPopoversOpen]; npp[index] = false; setPropietarioPopoversOpen(npp);
                          }}>
                            <Check className={`mr-2 h-4 w-4 ${propietario.contribuyente_id === c.id ? "opacity-100" : "opacity-0"}`} />
                            {c.display_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {index > 0 && (
                <Button type="button" variant="ghost" size="icon" onClick={() => { setPropietarios(propietarios.filter((_, i) => i !== index)); setPropietarioPopoversOpen(propietarioPopoversOpen.filter((_, i) => i !== index)); }} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-10 w-10" data-testid={`btn-eliminar-propietario-${index}`}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {index === 0 && (
            <div className="space-y-2">
              <Label>Año Ultima Solvencia Inmobiliaria</Label>
              <Input type="number" value={formData.anio_ultima_solvencia || ""} onChange={(e) => setFormData({...formData, anio_ultima_solvencia: parseInt(e.target.value) || null})} placeholder="Ej: 2024" data-testid="form-anio-ultima-solvencia" />
            </div>
          )}
          {index > 0 && <div className="md:col-span-1"></div>}
        </React.Fragment>
      ))}

      <div className="space-y-2">
        <Label>Fecha de Inscripcion</Label>
        <Input type="date" value={formData.fecha_inscripcion || ""} onChange={(e) => setFormData({...formData, fecha_inscripcion: e.target.value})} data-testid="form-fecha-inscripcion" />
      </div>

      <div className="space-y-2">
        <Label>Codigo Catastral <span className="text-red-500">*</span></Label>
        <Input value={formData.codigo_catastral || ""} onChange={(e) => { const valor = e.target.value.toUpperCase(); setFormData({...formData, codigo_catastral: valor}); setCodigoCatastralError(valor && !validarCodigoCatastral(valor) ? "Formato invalido. Use: X-XXXXXX (ej: 1-000001)" : ""); }} placeholder="Ej: 1-000001" data-testid="form-codigo-catastral" className={codigoCatastralError ? "border-red-500" : ""} maxLength={8} />
        {codigoCatastralError && <p className="text-xs text-red-500">{codigoCatastralError}</p>}
        <p className="text-xs text-slate-500">Formato: numero-6 digitos (ej: 1-000001)</p>
      </div>

      <div className="space-y-2">
        <Label>Tipo de Propiedad</Label>
        <Select value={formData.uso_propiedad || ""} onValueChange={(value) => setFormData({...formData, uso_propiedad: value})}>
          <SelectTrigger data-testid="form-tipo-propiedad"><SelectValue placeholder="Seleccione tipo de propiedad" /></SelectTrigger>
          <SelectContent>
            {["Vivienda","Oficina","Comercio","Apartamento","Cabaña","Townhouse","Industria","Terreno","Local/Vivienda","Galpon","Comunitario","Hotel","Colegio","Otro"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Propiedad</Label>
        <Select value={formData.propiedad || ""} onValueChange={(value) => setFormData({...formData, propiedad: value})}>
          <SelectTrigger data-testid="form-propiedad"><SelectValue placeholder="Seleccione tipo de propiedad" /></SelectTrigger>
          <SelectContent>
            {["Privado","Publico","Alquilado","Gubernamental","Otro"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Clasificador del Inmueble</Label>
        <Select value={formData.categoria_inmueble || ""} onValueChange={(value) => setFormData({...formData, categoria_inmueble: value})}>
          <SelectTrigger data-testid="form-categoria-inmueble"><SelectValue placeholder="Seleccione clasificador" /></SelectTrigger>
          <SelectContent>{opcionesClasificadorInmuebles.map((item) => <SelectItem key={item.id} value={item.uso_inmueble}>{item.uso_inmueble}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="space-y-2 flex items-end">
        <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200 w-full">
          <Checkbox id="clasificador-mixto" checked={formData.clasificador_mixto || false} onCheckedChange={(checked) => { setFormData({ ...formData, clasificador_mixto: checked, ...(!checked && { categoria_inmueble_2: "", area_terreno_m2_2: 0, tabla_pvt_2: "", valor_zona_2: 0, valor_catastral_terreno_me_2: 0, valor_catastral_terreno_bs_2: 0, area_construccion_m2_2: 0, tabla_tvc_2: "", zona_avaluo_2: "", valor_tipologia_2: 0, anio_inscripcion_inmueble_2: null, tabla_depreciacion_2: "", depreciacion_porcentaje_2: 0, valor_catastral_construccion_me_2: 0, valor_catastral_construccion_bs_2: 0 }) }); }} data-testid="check-clasificador-mixto" />
          <Label htmlFor="clasificador-mixto" className="text-indigo-800 font-medium cursor-pointer">Clasificador del Inmueble Mixto</Label>
        </div>
      </div>

      {formData.clasificador_mixto && (
        <div className="space-y-2">
          <Label>Clasificador del Inmueble 2</Label>
          <Select value={formData.categoria_inmueble_2 || ""} onValueChange={(value) => setFormData({...formData, categoria_inmueble_2: value})}>
            <SelectTrigger data-testid="form-categoria-inmueble-2"><SelectValue placeholder="Seleccione clasificador" /></SelectTrigger>
            <SelectContent>{opcionesClasificadorInmuebles.map((item) => <SelectItem key={item.id} value={item.uso_inmueble}>{item.uso_inmueble}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2 md:col-span-2">
        <Label>Direccion/Ubicacion</Label>
        <Input value={formData.direccion_ubicacion || ""} onChange={(e) => setFormData({...formData, direccion_ubicacion: e.target.value})} placeholder="Direccion completa" data-testid="form-direccion" />
      </div>

      <div className="space-y-2">
        <Label>Punto de Referencia</Label>
        <Input value={formData.punto_referencia || ""} onChange={(e) => setFormData({...formData, punto_referencia: e.target.value})} placeholder="Punto de referencia" data-testid="form-punto-ref" />
      </div>

      {/* Linderos */}
      <div className="md:col-span-3 border-b pb-2 mb-2 mt-4"><h3 className="font-semibold text-slate-700">Linderos</h3></div>

      {[1, 2, 3, 4].map(n => (
        <div key={n} className="md:col-span-3 grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Lindero {n}</Label>
            <Popover open={linderoOpen[n]} onOpenChange={(open) => setLinderoOpen({...linderoOpen, [n]: open})}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between h-10 font-normal" data-testid={`form-lindero-${n}`}>
                  {formData[`lindero_${n}`] || "Seleccione direccion..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar direccion..." value={linderoBusqueda} onValueChange={setLinderoBusqueda} />
                  <CommandList><CommandEmpty>No se encontro la direccion</CommandEmpty>
                    <CommandGroup>
                      {opcionesLindero.filter(opt => opt.label.toLowerCase().includes(linderoBusqueda.toLowerCase())).map((opt) => (
                        <CommandItem key={opt.value} value={opt.value} onSelect={() => { setFormData({...formData, [`lindero_${n}`]: opt.value}); setLinderoOpen({...linderoOpen, [n]: false}); setLinderoBusqueda(""); }}>
                          <Check className={`mr-2 h-4 w-4 ${formData[`lindero_${n}`] === opt.value ? "opacity-100" : "opacity-0"}`} />
                          {opt.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label>Descripcion Lindero {n}</Label>
            <Input value={formData[`descripcion_lindero_${n}`] || ""} onChange={(e) => setFormData({...formData, [`descripcion_lindero_${n}`]: e.target.value})} placeholder={`Descripcion lindero ${n}`} />
          </div>
        </div>
      ))}

      <div className="space-y-2">
        <Label>Exoneracion</Label>
        <Select value={formData.exoneracion || "NO"} onValueChange={(v) => setFormData({...formData, exoneracion: v})}>
          <SelectTrigger data-testid="form-exoneracion"><SelectValue placeholder="Seleccione" /></SelectTrigger>
          <SelectContent><SelectItem value="SI">SI</SelectItem><SelectItem value="NO">NO</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="space-y-2 md:col-span-2">
        <Label>Observaciones</Label>
        <Textarea value={formData.observaciones || ""} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} placeholder="Observaciones" rows={2} data-testid="form-observaciones" />
      </div>

      {/* Tipo Registro */}
      <div className="md:col-span-3 border-b pb-2 mb-2 mt-4"><h3 className="font-semibold text-slate-700">Tipo de Registro</h3></div>
      <div className="md:col-span-3 flex gap-6">
        <div className="flex items-center gap-2">
          <Checkbox id="tipo-anterior" checked={formData.tipo_registro === "Anterior"} onCheckedChange={(checked) => setFormData({...formData, tipo_registro: checked ? "Anterior" : ""})} />
          <Label htmlFor="tipo-anterior" className="cursor-pointer">Registro Actual</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="tipo-actual" checked={formData.tipo_registro === "Actual"} onCheckedChange={(checked) => setFormData({...formData, tipo_registro: checked ? "Actual" : ""})} />
          <Label htmlFor="tipo-actual" className="cursor-pointer">Registro Anterior</Label>
        </div>
      </div>

      {formData.tipo_registro === "Actual" && (
        <>
          <div className="space-y-2"><Label>Nro. de Registro</Label><Input value={formData.nro_registro || ""} onChange={(e) => setFormData({...formData, nro_registro: e.target.value})} placeholder="Nro. de registro" /></div>
          <div className="space-y-2"><Label>Folio</Label><Input value={formData.folio || ""} onChange={(e) => setFormData({...formData, folio: e.target.value})} placeholder="Folio" /></div>
          <div className="space-y-2"><Label>Tomo</Label><Input value={formData.tomo || ""} onChange={(e) => setFormData({...formData, tomo: e.target.value})} placeholder="Tomo" /></div>
          <div className="space-y-2"><Label>Fecha de Registro (dd/mm/aaaa)</Label><Input type="date" value={formData.fecha_registro || ""} onChange={(e) => setFormData({...formData, fecha_registro: e.target.value})} /></div>
          <div className="space-y-2"><Label>Trim</Label><Input value={formData.trim || ""} onChange={(e) => setFormData({...formData, trim: e.target.value})} placeholder="Trim" /></div>
          <div className="space-y-2"><Label>Protocolo</Label><Input value={formData.protocolo || ""} onChange={(e) => setFormData({...formData, protocolo: e.target.value})} placeholder="Protocolo" /></div>
        </>
      )}

      {formData.tipo_registro === "Anterior" && (
        <>
          <div className="space-y-2"><Label>Nro. de Registro</Label><Input value={formData.nro_registro_actual || ""} onChange={(e) => setFormData({...formData, nro_registro_actual: e.target.value})} placeholder="Nro. de registro" /></div>
          <div className="space-y-2"><Label>Folio</Label><Input value={formData.folio_actual || ""} onChange={(e) => setFormData({...formData, folio_actual: e.target.value})} placeholder="Folio" /></div>
          <div className="space-y-2"><Label>Fecha de Registro (dd/mm/aaaa)</Label><Input type="date" value={formData.fecha_registro_actual || ""} onChange={(e) => setFormData({...formData, fecha_registro_actual: e.target.value})} /></div>
          <div className="space-y-2"><Label>Asiento Registral</Label><Input value={formData.asiento_registral || ""} onChange={(e) => setFormData({...formData, asiento_registral: e.target.value})} placeholder="Asiento registral" /></div>
          <div className="space-y-2 md:col-span-2"><Label>Inmueble Matriculado</Label><Input value={formData.inmueble_matriculado || ""} onChange={(e) => setFormData({...formData, inmueble_matriculado: e.target.value})} placeholder="Inmueble matriculado" /></div>
        </>
      )}

      {/* Avaluo */}
      <div className="md:col-span-3 border-b pb-2 mb-2 mt-4">
        <h3 className="font-semibold text-slate-700">
          Avaluo {formData.clasificador_mixto && formData.categoria_inmueble ? <span className="text-blue-600 ml-1">({formData.categoria_inmueble})</span> : null}
        </h3>
      </div>

      <div className="space-y-2"><Label>Area de Terreno en m2</Label><Input type="number" step="0.01" value={formData.area_terreno_m2 || ""} onChange={(e) => setFormData({...formData, area_terreno_m2: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" /></div>
      <div className="space-y-2">
        <Label>Tabla PVT (Clasificacion del Terreno)</Label>
        <Select value={formData.tabla_pvt || ""} onValueChange={(value) => { const tcmmv = obtenerTCMMVPorClasificacion(value); setFormData({...formData, tabla_pvt: value, valor_zona: tcmmv}); }}>
          <SelectTrigger data-testid="form-tabla-pvt"><SelectValue placeholder="Seleccione clasificacion" /></SelectTrigger>
          <SelectContent>
            {opcionesTablaPVT.length === 0 ? <div className="px-2 py-4 text-sm text-slate-500 text-center">No hay clasificaciones registradas</div> : opcionesTablaPVT.map((clasificacion) => { const tcmmv = obtenerTCMMVPorClasificacion(clasificacion); return <SelectItem key={clasificacion} value={clasificacion}>{clasificacion} {tcmmv > 0 && <span className="text-slate-500 ml-2">(TCMMV: {tcmmv.toFixed(2)})</span>}</SelectItem>; })}
          </SelectContent>
        </Select>
        {opcionesTablaPVT.length === 0 && <p className="text-xs text-amber-600">Registre clasificaciones en "Tabla Planta de Valores de la Tierra"</p>}
      </div>
      <div className="space-y-2"><Label>Valor Zona (TCMMV/m2)</Label><Input type="number" step="0.01" value={formData.valor_zona || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /><p className="text-xs text-slate-500">Se carga automaticamente segun la clasificacion PVT</p></div>
      <div className="space-y-2"><Label>Valor Catastral Terreno (M.E.)</Label><Input type="number" step="0.01" value={formData.valor_catastral_terreno_me?.toFixed(2) || "0.00"} readOnly placeholder="0.00" className="text-right bg-slate-50" /><p className="text-xs text-slate-500">= Area Terreno (m2) x Valor Zona (TCMMV/m2)</p></div>
      <div className="space-y-2"><Label>Valor Catastral Terreno (Bs.)</Label><Input type="number" step="0.01" value={formData.valor_catastral_terreno_bs?.toFixed(2) || "0.00"} readOnly placeholder="0.00" className="text-right bg-slate-50" /><p className="text-xs text-slate-500">= Valor Cat. Terreno (M.E.) x Unidad de Cuenta</p></div>

      {/* Sin Construccion */}
      <div className="space-y-2 md:col-span-3 flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
        <Checkbox id="sin-construccion" checked={formData.sin_construccion || false} onCheckedChange={(checked) => { if (checked) { setFormData({...formData, sin_construccion: true, area_construccion_m2: 0, tabla_tvc: "SIN CONSTRUCCION - SIN CONSTRUCCION", zona_avaluo: "F", anio_inscripcion_inmueble: new Date().getFullYear()}); } else { setFormData({...formData, sin_construccion: false}); }}} data-testid="check-sin-construccion" />
        <Label htmlFor="sin-construccion" className="text-amber-800 font-medium cursor-pointer">Sin Construccion?</Label>
      </div>

      <div className="space-y-2"><Label>Area de Construccion en m2</Label><Input type="number" step="0.01" value={formData.area_construccion_m2 || ""} onChange={(e) => setFormData({...formData, area_construccion_m2: parseFloat(e.target.value) || 0})} placeholder="0.00" className={`text-right ${formData.sin_construccion ? 'bg-slate-100' : ''}`} disabled={formData.sin_construccion} /></div>
      <div className="space-y-2">
        <Label>Tabla TVC (Tipologia - Descripcion)</Label>
        {formData.sin_construccion ? <Input value={formData.tabla_tvc || ""} readOnly className="bg-slate-100" /> : (
        <Popover open={tablaTVCOpen} onOpenChange={setTablaTVCOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" className="w-full justify-between h-auto min-h-10 font-normal text-left" data-testid="form-tabla-tvc">
              <span className="truncate">{formData.tabla_tvc || "Seleccione tipologia..."}</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[500px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Buscar tipologia o descripcion..." value={tablaTVCBusqueda} onValueChange={setTablaTVCBusqueda} />
              <CommandList><CommandEmpty>No se encontraron resultados</CommandEmpty>
                <CommandGroup>
                  {opcionesTablaTVC.length === 0 ? <div className="px-2 py-4 text-sm text-slate-500 text-center">No hay registros en Tabla TVC</div> : opcionesTablaTVC.filter(opcion => opcion.valor.toLowerCase().includes(tablaTVCBusqueda.toLowerCase())).map((opcion) => (
                    <CommandItem key={opcion.id} value={opcion.valor} onSelect={() => { const valorTip = obtenerValorTipologia(opcion.valor, formData.zona_avaluo); setFormData({...formData, tabla_tvc: opcion.valor, valor_tipologia: valorTip}); setTablaTVCOpen(false); setTablaTVCBusqueda(""); }}>
                      <Check className={`mr-2 h-4 w-4 ${formData.tabla_tvc === opcion.valor ? "opacity-100" : "opacity-0"}`} />
                      <span className="truncate">{opcion.valor}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        )}
        {opcionesTablaTVC.length === 0 && !formData.sin_construccion && <p className="text-xs text-amber-600">Registre datos en "Tabla de Valores de la Construccion"</p>}
      </div>
      <div className="space-y-2">
        <Label>Zona</Label>
        {formData.sin_construccion ? <Input value={formData.zona_avaluo || ""} readOnly className="bg-slate-100" /> : (
        <Select value={formData.zona_avaluo || ""} onValueChange={(value) => { const valorTip = obtenerValorTipologia(formData.tabla_tvc, value); setFormData({...formData, zona_avaluo: value, valor_tipologia: valorTip}); }}>
          <SelectTrigger data-testid="form-zona"><SelectValue placeholder="Seleccione zona" /></SelectTrigger>
          <SelectContent>{["A","B","C","D","E","F"].map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
        </Select>
        )}
      </div>
      <div className="space-y-2"><Label>Valor Tipologia</Label><Input type="number" step="0.01" value={formData.valor_tipologia || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /><p className="text-xs text-slate-500">Se carga automaticamente segun Tabla TVC y Zona</p></div>
      <div className="space-y-2"><Label>Año de Inscripcion Inmueble</Label><Input type="number" value={formData.anio_inscripcion_inmueble || ""} readOnly placeholder="Se carga automaticamente" data-testid="form-anio-inscripcion" className="bg-slate-50" /><p className="text-xs text-slate-500">Se carga automaticamente segun la fecha de registro</p></div>
      <div className="space-y-2"><Label>Tabla Depreciacion</Label><Input value={formData.tabla_depreciacion || ""} readOnly placeholder="Se calcula automaticamente" className="bg-slate-50" data-testid="form-tabla-depreciacion" /><p className="text-xs text-slate-500">Se carga automaticamente segun Año de Inscripcion</p></div>
      <div className="space-y-2"><Label>Depreciacion (%)</Label><Input type="number" step="0.01" value={formData.depreciacion_porcentaje || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" data-testid="form-depreciacion-porcentaje" /><p className="text-xs text-slate-500">Se carga automaticamente segun Año de Inscripcion</p></div>
      <div className="space-y-2"><Label>Valor Catastral Construccion (M.E.)</Label><Input type="number" step="0.01" value={formData.valor_catastral_construccion_me || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" data-testid="form-valor-catastral-construccion-me" /><p className="text-xs text-slate-500">= (Area Construccion x Valor Tipologia) - Depreciacion%</p></div>
      <div className="space-y-2"><Label>Valor Catastral Construccion (Bs.)</Label><Input type="number" step="0.01" value={formData.valor_catastral_construccion_bs || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" data-testid="form-valor-catastral-construccion-bs" /><p className="text-xs text-slate-500">= Valor Cat. Construccion (M.E.) x Unidad de Cuenta</p></div>
      <div className="space-y-2"><Label>Valor Catastral Inmueble (M.E.)</Label><Input type="number" step="0.01" value={formData.valor_catastral_inmueble_me || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" data-testid="form-valor-catastral-inmueble-me" /><p className="text-xs text-slate-500">= Valor Cat. Terreno (M.E.) + Valor Cat. Construccion (M.E.)</p></div>
      <div className="space-y-2"><Label>Valor Catastral Inmueble (Bs.)</Label><Input type="number" step="0.01" value={formData.valor_catastral_inmueble_bs || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" data-testid="form-valor-catastral-inmueble-bs" /><p className="text-xs text-slate-500">= Valor Cat. Inmueble (M.E.) x Unidad de Cuenta</p></div>

      {/* Avaluo 2 - Clasificador Mixto */}
      {formData.clasificador_mixto && (
        <>
          <div className="md:col-span-3 border-b pb-2 mb-2 mt-4 bg-indigo-50 p-2 rounded">
            <h3 className="font-semibold text-indigo-700">Avaluo 2{formData.categoria_inmueble_2 ? ` (${formData.categoria_inmueble_2})` : ''}</h3>
          </div>
          <div className="space-y-2"><Label>Area de Terreno en m2 (2)</Label><Input type="number" step="0.01" value={formData.area_terreno_m2_2 || ""} onChange={(e) => setFormData({...formData, area_terreno_m2_2: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" /></div>
          <div className="space-y-2">
            <Label>Tabla PVT (2)</Label>
            <Select value={formData.tabla_pvt_2 || ""} onValueChange={(value) => { const tcmmv = obtenerTCMMVPorClasificacion(value); setFormData({...formData, tabla_pvt_2: value, valor_zona_2: tcmmv}); }}>
              <SelectTrigger data-testid="form-tabla-pvt-2"><SelectValue placeholder="Seleccione clasificacion" /></SelectTrigger>
              <SelectContent>{opcionesTablaPVT.map((c) => { const tcmmv = obtenerTCMMVPorClasificacion(c); return <SelectItem key={c} value={c}>{c} {tcmmv > 0 && <span className="text-slate-500 ml-2">(TCMMV: {tcmmv.toFixed(2)})</span>}</SelectItem>; })}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Valor Zona (TCMMV/m2) (2)</Label><Input type="number" step="0.01" value={formData.valor_zona_2 || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>
          <div className="space-y-2"><Label>Valor Catastral Terreno (M.E.) (2)</Label><Input type="number" step="0.01" value={formData.valor_catastral_terreno_me_2?.toFixed(2) || "0.00"} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>
          <div className="space-y-2"><Label>Valor Catastral Terreno (Bs.) (2)</Label><Input type="number" step="0.01" value={formData.valor_catastral_terreno_bs_2?.toFixed(2) || "0.00"} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>
          <div className="space-y-2"><Label>Area de Construccion en m2 (2)</Label><Input type="number" step="0.01" value={formData.area_construccion_m2_2 || ""} onChange={(e) => setFormData({...formData, area_construccion_m2_2: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" /></div>
          <div className="space-y-2">
            <Label>Tabla TVC (2)</Label>
            <Select value={formData.tabla_tvc_2 || ""} onValueChange={(value) => { const partes = value.split(" - "); const tipologia = partes[0]; const zona = formData.zona_avaluo_2 || "A"; const valorTip = obtenerValorTipologiaPorZona(tipologia, zona); setFormData({...formData, tabla_tvc_2: value, valor_tipologia_2: valorTip}); }}>
              <SelectTrigger data-testid="form-tabla-tvc-2"><SelectValue placeholder="Seleccione tipologia" /></SelectTrigger>
              <SelectContent>{opcionesTablaTVC.map((opt) => <SelectItem key={opt.id} value={opt.valor}>{opt.valor}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Zona (2)</Label>
            <Select value={formData.zona_avaluo_2 || ""} onValueChange={(v) => { const tipologia = formData.tabla_tvc_2?.split(" - ")[0] || ""; const valorTip = obtenerValorTipologiaPorZona(tipologia, v); setFormData({...formData, zona_avaluo_2: v, valor_tipologia_2: valorTip}); }}>
              <SelectTrigger data-testid="form-zona-2"><SelectValue placeholder="Seleccione zona" /></SelectTrigger>
              <SelectContent>{["A","B","C","D","E","F"].map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Valor Tipologia (2)</Label><Input type="number" step="0.01" value={formData.valor_tipologia_2 || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>
          <div className="space-y-2"><Label>Año de Inscripcion (2)</Label><Input type="number" value={formData.anio_inscripcion_inmueble_2 || ""} onChange={(e) => setFormData({...formData, anio_inscripcion_inmueble_2: parseInt(e.target.value) || null})} placeholder="Ej: 2015" /></div>
          <div className="space-y-2"><Label>Tabla Depreciacion (2)</Label><Input value={formData.tabla_depreciacion_2 || ""} readOnly placeholder="Se calcula automaticamente" className="bg-slate-50" /></div>
          <div className="space-y-2"><Label>Depreciacion (%) (2)</Label><Input type="number" step="0.01" value={formData.depreciacion_porcentaje_2 || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>
          <div className="space-y-2"><Label>Valor Catastral Construccion (M.E.) (2)</Label><Input type="number" step="0.01" value={formData.valor_catastral_construccion_me_2 || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>
          <div className="space-y-2"><Label>Valor Catastral Construccion (Bs.) (2)</Label><Input type="number" step="0.01" value={formData.valor_catastral_construccion_bs_2 || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>
          <div className="space-y-2"><Label>Valor Catastral Inmueble (M.E.) (2)</Label><Input type="number" step="0.01" value={formData.valor_catastral_inmueble_me_2 || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>
          <div className="space-y-2"><Label>Valor Catastral Inmueble (Bs.) (2)</Label><Input type="number" step="0.01" value={formData.valor_catastral_inmueble_bs_2 || ""} readOnly placeholder="0.00" className="text-right bg-slate-50" /></div>

          {/* Total Combinado */}
          <div className="md:col-span-3 border-t pt-4 mt-4 bg-green-50 p-3 rounded">
            <h3 className="font-semibold text-green-700 mb-3">Total Combinado (Avaluo 1 + Avaluo 2)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-green-700">Valor Catastral Total (M.E.)</Label>
                <Input type="number" value={((formData.valor_catastral_inmueble_me || 0) + (formData.valor_catastral_inmueble_me_2 || 0)).toFixed(2)} readOnly className="text-right bg-green-100 font-bold text-green-800" />
              </div>
              <div className="space-y-1">
                <Label className="text-green-700">Valor Catastral Total (Bs.)</Label>
                <Input type="number" value={((formData.valor_catastral_inmueble_bs || 0) + (formData.valor_catastral_inmueble_bs_2 || 0)).toFixed(2)} readOnly className="text-right bg-green-100 font-bold text-green-800" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // ==================== RENDER PRINCIPAL ====================

  return (
    <div className="space-y-6" data-testid="catastro-submodulo">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/catastro')} className="hover:bg-slate-100">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
          <FileText className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Ficha Catastral</h1>
          <p className="text-slate-500">Gestion de ficha catastral</p>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
              <span className="font-bold text-blue-800">{data.length}</span>
              <span className="text-blue-600">de</span>
              <span className="font-bold text-blue-800">{totalRecords}</span>
              <span className="text-blue-600">registros</span>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={() => setColumnFilters({ nro_ficha: "", codigo_catastral: "", rif: "", propietario: "", categoria_inmueble: "", fecha_inscripcion: "" })} className="text-xs text-slate-500 hover:text-slate-700">
                Limpiar filtros
              </Button>
            )}
            <PaginationControls />
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="btn-exportar"><Download className="w-4 h-4 mr-2" />Exportar</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportFichaToExcel} data-testid="btn-export-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={exportFichaToPDF} data-testid="btn-export-pdf"><File className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={exportFichaToHTML} data-testid="btn-export-html"><FileType className="w-4 h-4 mr-2" />HTML</DropdownMenuItem>
                <DropdownMenuItem onClick={exportFichaToCSV} data-testid="btn-export-csv"><FileSpreadsheet className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={exportFichaToText} data-testid="btn-export-text"><File className="w-4 h-4 mr-2" />Texto</DropdownMenuItem>
                <DropdownMenuItem onClick={exportFichaToJSON} data-testid="btn-export-json"><FileJson className="w-4 h-4 mr-2" />JSON</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo">
              <Plus className="w-4 h-4 mr-2" />Nueva Ficha
            </Button>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nro. Ficha</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Codigo Catastral</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">RIF</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Propietario</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Categoria Inmueble</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Fecha Inscripcion</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-slate-600">Acciones</th>
                  </tr>
                  <tr className="border-b bg-white">
                    <th className="px-2 py-1"><Input placeholder="Filtrar..." value={columnFilters.nro_ficha} onChange={(e) => setColumnFilters({...columnFilters, nro_ficha: e.target.value})} className="h-7 text-xs" data-testid="filter-nro-ficha" /></th>
                    <th className="px-2 py-1"><Input placeholder="Filtrar..." value={columnFilters.codigo_catastral} onChange={(e) => setColumnFilters({...columnFilters, codigo_catastral: e.target.value})} className="h-7 text-xs" data-testid="filter-codigo-catastral" /></th>
                    <th className="px-2 py-1"><Input placeholder="Filtrar..." value={columnFilters.rif} onChange={(e) => setColumnFilters({...columnFilters, rif: e.target.value})} className="h-7 text-xs" data-testid="filter-rif" /></th>
                    <th className="px-2 py-1"><Input placeholder="Filtrar..." value={columnFilters.propietario} onChange={(e) => setColumnFilters({...columnFilters, propietario: e.target.value})} className="h-7 text-xs" data-testid="filter-propietario" /></th>
                    <th className="px-2 py-1"><Input placeholder="Filtrar..." value={columnFilters.categoria_inmueble} onChange={(e) => setColumnFilters({...columnFilters, categoria_inmueble: e.target.value})} className="h-7 text-xs" data-testid="filter-categoria" /></th>
                    <th className="px-2 py-1"><Input placeholder="Filtrar..." value={columnFilters.fecha_inscripcion} onChange={(e) => setColumnFilters({...columnFilters, fecha_inscripcion: e.target.value})} className="h-7 text-xs" data-testid="filter-fecha" /></th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />Cargando...
                    </td></tr>
                  ) : data.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No hay registros</td></tr>
                  ) : (
                    data.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.nro_ficha}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{item.codigo_catastral}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{item.rif}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{item.nombre_razon_social}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{item.categoria_inmueble}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{item.fecha_inscripcion}</td>
                        <td className="px-4 py-3 text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`row-actions-${item.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(item)}><Edit className="w-4 h-4 mr-2" />Editar</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handlePrintFichaCatastral(item)} data-testid={`btn-imprimir-${item.id}`}><Printer className="w-4 h-4 mr-2" />Imprimir</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Eliminar</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Paginación inferior */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-2">
            <span className="text-sm text-slate-500">
              Pagina {currentPage + 1} de {totalPages} ({totalRecords} registros)
            </span>
            <PaginationControls />
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Ficha Catastral" : "Nuevo Registro - Ficha Catastral"}</DialogTitle>
          </DialogHeader>
          <div className="py-4">{renderFichaCatastralForm()}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={editingItem ? handleUpdate : handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar">
              {editingItem ? "Actualizar" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FichaCatastral;
